import React, { useState, useEffect } from 'react';
import styled from 'astroturf/react';

import { ButtonLink } from 'js/design_system/link/ButtonLink';
import Modal from 'js/components/modals/Modal';
import ReaderMessageForm, {
  fetchReaderMessageLimit,
} from 'js/core_webpack/sitewide_business_elements/download/reader_message_form.wjs.jsx';
import { Work } from 'js/liswp/types';
import { UserWithThumbnail } from 'js/core_post_webpack/models/user';
import CitationModal from 'js/journals/CitationModal';
import type { PublishedLetterMetadata } from 'js/journals/CitationButton';
import { ImpressionWatcher } from 'js/utils/impressions/ImpressionWatcher';
import { ImpressionEntity } from 'js/utils/impressions/watchImpressions';
import trackClick from 'js/utils/trackClick';

const ReasonsForDownloadingModal = styled(Modal)`
  width: 700px;
  padding: 0;
`;

// Hacky solution to address a width issue with the button
const DownloadButtonContainer = styled('div')`
  width: calc(100% - 64px);
`;

type Props = {
  author: UserWithThumbnail | null;
  downloadUrl: string;
  reactionPrompt?: string;
  work: Work | null;
  displayClass: string;
  attachmentId: number;
  citations: { name: string; citation: string }[];
  publishedLetterId: number;
  publishedLetterMetadata: PublishedLetterMetadata;
  postDownloadBucket?: 'reader_message' | 'citation' | null;
  loggedIn: boolean;
};

const DownloadButton = ({
  author,
  downloadUrl,
  reactionPrompt,
  work,
  displayClass,
  attachmentId,
  citations,
  publishedLetterId,
  publishedLetterMetadata,
  postDownloadBucket,
  loggedIn,
}: Props) => {
  const [readerMessageLimitReached, setReaderMessageLimitReached] =
    useState<boolean>(true);
  const [modalShown, setModalShown] = useState<boolean>(false);

  useEffect(() => {
    let cancelled = false;
    setReaderMessageLimitReached(true);
    if (!work || !loggedIn) return;

    fetchReaderMessageLimit(work.id)
      .then((limitReached: boolean) => {
        if (!cancelled) setReaderMessageLimitReached(limitReached);
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [work, attachmentId, loggedIn]);

  const eligibleForPrompt =
    loggedIn &&
    !readerMessageLimitReached &&
    work &&
    author &&
    (!postDownloadBucket || citations.length > 0);
  const citationBucket = postDownloadBucket === 'citation';

  const trackDownloadAction = (source: string) => {
    if (!postDownloadBucket || !work || citations.length === 0) return;
    trackClick({
      source,
      bucket: postDownloadBucket,
      entityId: publishedLetterId,
      extra: { work_id: work.id, logged_in: loggedIn },
    });
  };

  const handleDownloadClick = () => {
    if (!eligibleForPrompt) return;
    trackDownloadAction('sap_post_download_opportunity');
    setModalShown(true);
  };

  return (
    <DownloadButtonContainer>
      <ButtonLink
        className={displayClass}
        onClick={handleDownloadClick}
        href={downloadUrl}
        icon="download"
      >
        Download
      </ButtonLink>
      {modalShown && citationBucket && work && (
        <CitationModal
          open
          citations={citations}
          onClose={() => setModalShown(false)}
          entity_id={publishedLetterId}
          source="sap_post_download_citation"
          work={{
            ...work,
            authors: publishedLetterMetadata.authors,
          }}
          publishedLetterMetadata={publishedLetterMetadata}
          modalTitle="The citation, in case you need it"
        />
      )}
      {modalShown && !citationBucket && work && author && (
        <ReasonsForDownloadingModal onClose={() => setModalShown(false)}>
          <ImpressionWatcher
            active={postDownloadBucket === 'reader_message'}
            source="sap_post_download_reader_message"
            entity_type={ImpressionEntity.UnMapped}
            entity_id={publishedLetterId}
          >
            <ReaderMessageForm
              primaryAuthor={{
                displayName: author.display_name,
                photo: author.photo,
              }}
              workId={work.id}
              attachmentId={attachmentId}
              reactionPrompt={reactionPrompt}
              readerMessageSource="single-article-page"
              showMessageToast={false}
              onMessageSent={() =>
                trackDownloadAction('sap_reader_message_sent')
              }
              setReaderMessageLimitReached={setReaderMessageLimitReached}
              closeModal={() => {
                setModalShown(false);
              }}
            />
          </ImpressionWatcher>
        </ReasonsForDownloadingModal>
      )}
    </DownloadButtonContainer>
  );
};

export default DownloadButton;
